import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Upload, Trash2, Loader2, BookOpen, FileText, GraduationCap, Calendar, Users } from 'lucide-react';
import { toast } from 'sonner';

const AdminHistoricalRecordsPage = () => {
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [year, setYear] = useState('');
  const [seasonName, setSeasonName] = useState('');
  const [file, setFile] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/admin/historical-records');
      setSeasons(res.data);
    } catch { toast.error('فشل تحميل البيانات'); }
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !year || !seasonName) {
      toast.error('يرجى ملء جميع الحقول واختيار ملف Excel');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('year', year);
      formData.append('season_name', seasonName);
      const res = await api.post('/admin/historical-records/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${res.data.message} - ${res.data.readers_count} قارئ، ${res.data.total_items} عنصر`);
      setFile(null);
      setYear('');
      setSeasonName('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل رفع الملف');
    } finally { setUploading(false); }
  };

  const handleDelete = async (yr) => {
    if (!window.confirm(`هل تريد حذف سجلات سنة ${yr}؟`)) return;
    try {
      await api.delete(`/admin/historical-records/${yr}`);
      toast.success('تم الحذف بنجاح');
      load();
    } catch { toast.error('فشل الحذف'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-16 h-16 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-historical-records">
      <div>
        <h1 className="text-4xl font-bold text-accent">السجل التاريخي</h1>
        <p className="text-muted-foreground text-lg mt-1">رفع وإدارة بيانات المنجزات القرائية للمواسم السابقة</p>
      </div>

      {/* Upload Form */}
      <Card className="p-6 border-2 border-dashed border-primary/30 bg-primary/5">
        <form onSubmit={handleUpload} className="space-y-4">
          <h3 className="text-xl font-bold text-accent flex items-center gap-2">
            <Upload className="w-5 h-5 text-primary" />
            رفع بيانات موسم جديد
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold text-accent">السنة الهجرية</Label>
              <Input
                value={year}
                onChange={e => setYear(e.target.value)}
                placeholder="مثال: 1446"
                className="mt-1"
                data-testid="year-input"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-accent">اسم الموسم</Label>
              <Input
                value={seasonName}
                onChange={e => setSeasonName(e.target.value)}
                placeholder="مثال: الموسم الثاني"
                className="mt-1"
                data-testid="season-name-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold text-accent">ملف Excel</Label>
            <div className="mt-1 flex items-center gap-3">
              <label className="flex items-center gap-2 px-4 py-2 border-2 border-primary/30 rounded-xl cursor-pointer hover:bg-primary/10 transition-colors">
                <Upload className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-accent">
                  {file ? file.name : 'اختر ملف .xlsx'}
                </span>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={e => setFile(e.target.files[0])}
                  data-testid="file-input"
                />
              </label>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              يجب أن يحتوي الملف على أوراق منفصلة للكتب والمقالات والدراسات العلمية.
              كل ورقة تحتوي على أعمدة: رقم القارئ، اسم القارئ، العنوان، المؤلف، عدد الصفحات، تاريخ الختم
            </p>
          </div>

          <Button type="submit" disabled={uploading} className="bg-primary hover:bg-primary/90" data-testid="upload-btn">
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin ml-2" /> جارٍ الرفع...</> : <><Upload className="w-4 h-4 ml-2" /> رفع البيانات</>}
          </Button>
        </form>
      </Card>

      {/* Seasons List */}
      <div>
        <h3 className="text-xl font-bold text-accent mb-4">المواسم المرفوعة</h3>
        {seasons.length === 0 ? (
          <Card className="p-12 text-center">
            <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-muted-foreground">لا توجد مواسم مرفوعة بعد</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {seasons.map((s) => (
              <Card key={s.year} className="p-5 hover:shadow-md transition-shadow" data-testid={`season-${s.year}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-bold text-accent text-lg">{s.season_name} ({s.year}هـ)</p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                        <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {s.readers_count} قارئ</span>
                        <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> {s.total_books} كتاب</span>
                        <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {s.total_articles} مقالة</span>
                        <span className="flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" /> {s.total_studies} دراسة</span>
                      </div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(s.year)}
                    className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-season-${s.year}`}>
                    <Trash2 className="w-4 h-4 ml-1" /> حذف
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminHistoricalRecordsPage;
